import { Transition } from '@headlessui/react';
import _ from 'lodash';
import { ChangeEvent, Dispatch, Fragment, memo, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import { Button, Card, IconSvg, Input, Text } from '@/lib/v2/components';
import { CloseIcon } from '@/lib/v2/icons/outline';
import { EditPencilIcon } from '@/lib/v2/icons/solid';

import { ISegmentTableRow } from '@/src/ContactsModule/interfaces/Interfaces';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';

import { SEGMENT_PATH } from '@/modules/ContactsModule/constants';
import {
  useMutationCreateSegmentContext,
  useStateCreateSegmentContext,
} from '@/modules/ContactsModule/contexts/CreateSegmentContext';

interface RenameSegmentProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  leftClick: number;
  topClick: number;
  setSegment?: Dispatch<SetStateAction<ISegmentTableRow | undefined>>;
  value?: string;
}

const RenameSegment = ({
  isOpen,
  onClose,
  leftClick,
  topClick,
  setSegment,
  value,
}: RenameSegmentProps) => {
  const [name, setName] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const left = `${leftClick + 60}px`;
  const top = `${topClick}px`;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setSegmentName } = useMutationCreateSegmentContext();
  const { isEditPath, segmentName } = useStateCreateSegmentContext();

  const isDetailSegmentPath = useMatch('/v2/contacts/segments/:segmentId');

  const service = useService();
  const { segmentId } = useParams();

  const handleApply = async () => {
    setIsEditing(true);
    if (isDetailSegmentPath && segmentId) {
      const renameResponse = await service?.renameSegment(name, Number(segmentId));

      if (renameResponse)
        setSegment?.((prevSegment) => {
          const newSegment = _.cloneDeep(prevSegment);
          newSegment && (newSegment.name = name);
          return newSegment;
        });
      setIsEditing(false);
      onClose(false);
      return;
    }
    setIsEditing(false);
    onClose(false);
    if (isEditPath) setSegmentName(name);
    else navigate(`${SEGMENT_PATH}/create-segment/${name}`);
  };

  const segmentCurrentName = value ?? segmentName;

  useEffect(() => {
    if (segmentCurrentName.length > 0) setName(segmentCurrentName);
  }, [segmentCurrentName, segmentName, value]);

  return (
    <Transition
      appear
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={isOpen}
    >
      <div className="fixed z-10 w-[380px]" style={{ left, top }}>
        <Card>
          <div className="flex w-full flex-col items-center gap-3 text-center">
            <button
              className="absolute right-4 top-4 z-10 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2
                      focus:ring-emblue-primary focus:ring-offset-2"
              type="button"
              onClick={() => onClose(false)}
            >
              <span className="sr-only">Close</span>
              <IconSvg svgComponent={<CloseIcon />} />
            </button>
            <div className="flex w-full flex-col">
              <div className="flex gap-2">
                <EditPencilIcon />
                <Text>{t('ITEMS_FILTERS.rename')}</Text>
              </div>
              <Input
                placeHolder={t('ITEMS_FILTERS.renameSegment')}
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              />
            </div>
            <div className="flex w-full">
              <Button
                disabled={name === segmentCurrentName || name.length === 0}
                isLoading={isEditing}
                onClick={() => void handleApply()}
              >
                {t('ITEMS_FILTERS.apply')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Transition>
  );
};

export default memo(RenameSegment);
